
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Edit, Trash2 } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';

interface GroupedReport {
  authorId: string;
  year: number;
  quarter: number;
  reports: any[];
  totalSales: number;
  totalAmount: number;
  status: string;
  countries: Set<string>;
}

interface RoyaltyReportsTableProps {
  filteredReports: GroupedReport[];
  authors?: Array<{ id: string; name: string }>;
  onViewReport: (group: GroupedReport) => void;
  onEditReport: (group: GroupedReport) => void;
  onDeleteReport: (group: GroupedReport) => void;
}

const RoyaltyReportsTable: React.FC<RoyaltyReportsTableProps> = ({
  filteredReports,
  authors,
  onViewReport,
  onEditReport,
  onDeleteReport
}) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Period</TableHead>
          <TableHead>Author</TableHead>
          <TableHead>Books</TableHead>
          <TableHead>Countries</TableHead>
          <TableHead className="text-right">Total Sales</TableHead>
          <TableHead className="text-right">Total Amount</TableHead>
          <TableHead className="text-center">Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {filteredReports.map((group, index) => {
          const author = authors?.find(a => a.id === group.authorId);
          const uniqueBooks = new Set(group.reports.map(r => r.books?.title).filter(Boolean));
          const countriesArray = Array.from(group.countries);
          
          return (
            <TableRow key={`${group.authorId}-${group.year}-${group.quarter}-${index}`}>
              <TableCell>Q{group.quarter} {group.year}</TableCell>
              <TableCell className="font-medium">{author?.name || 'Unknown'}</TableCell>
              <TableCell>{uniqueBooks.size} book{uniqueBooks.size !== 1 ? 's' : ''}</TableCell>
              <TableCell>{countriesArray.join(', ')}</TableCell>
              <TableCell className="text-right">{group.totalSales.toLocaleString()}</TableCell>
              <TableCell className="text-right">{formatCurrency(group.totalAmount)}</TableCell>
              <TableCell className="text-center">
                <Badge 
                  variant={group.status === 'published' ? 'default' : 'secondary'}
                >
                  {group.status}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost" 
                    size="sm"
                    onClick={() => onViewReport(group)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="ghost" 
                    size="sm"
                    onClick={() => onEditReport(group)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost" 
                    size="sm"
                    onClick={() => onDeleteReport(group)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default RoyaltyReportsTable;
